// pages/control/ControlEditOrder.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useControlAuth } from '../../ControlAuthContext';
import './ControlEditOrder.css';

const ControlEditOrder = () => {
  const { id } = useParams();
  const { user } = useControlAuth();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [controlUsers, setControlUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Form states
  const [date, setDate] = useState('');
  const [week, setWeek] = useState('');
  const [trimester, setTrimester] = useState('');
  const [orderid, setOrderid] = useState('');
  const [idclient, setIdclient] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [comments, setComments] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [margins, setMargins] = useState('');
  const [commission, setCommission] = useState('');
  const [userid, setUserid] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('controlToken');
      try {
        // Fetch order details
        const orderResponse = await axios.get(`https://valleyapi-dr9y.onrender.com/api/control/orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fetchedOrder = orderResponse.data.order;
        setOrder(fetchedOrder);
        setItems(orderResponse.data.items);

        // Populate form fields
        setDate(fetchedOrder.date.split('T')[0]); // Assuming date is ISO
        setWeek(fetchedOrder.week);
        setTrimester(fetchedOrder.trimester);
        setOrderid(fetchedOrder.orderid);
        setIdclient(fetchedOrder.idclient);
        setDescription(fetchedOrder.description);
        setStatus(fetchedOrder.status);
        setComments(fetchedOrder.comments);
        setDeliveryDate(fetchedOrder.delivery_date ? fetchedOrder.delivery_date.split('T')[0] : '');
        setMargins(fetchedOrder.margins || '');
        setCommission(fetchedOrder.commission || '');
        setUserid(fetchedOrder.userid);

        // Fetch clients for select
        const clientsResponse = await axios.get('https://valleyapi-dr9y.onrender.com/api/control/orders/clients', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClients(clientsResponse.data.clients);

        if (user.accesstype === 0) {
          const usersRes = await axios.get('https://valleyapi-dr9y.onrender.com/api/control/orders/users', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setControlUsers(usersRes.data.users || []);
        }
      } catch (err) {
        if (err.response?.status === 403) {
          navigate('/control');
        } else {
          setError('Failed to fetch data');
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchData();
    }
  }, [user, id, navigate]);

  useEffect(() => {
    if (date) {
      const d = new Date(date);
      // Calculate ISO week number
      const weekNumber = Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + new Date(d.getFullYear(), 0, 1).getDay() + 1) / 7);
      setWeek(weekNumber);
      const month = d.getMonth();
      const trimesterVal = Math.floor(month / 3) + 1;
      setTrimester(trimesterVal);
    }
  }, [date]);

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems([...items, { product: '', brand: '', description: '', cost: '', price: '', quantity: '' }]);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (window.confirm('Are you sure you want to update this order?')) {
      const token = localStorage.getItem('controlToken');
      try {
        await axios.put(`https://valleyapi-dr9y.onrender.com/api/control/orders/${id}`, {
          date,
          week,
          trimester,
          orderid,
          idclient,
          description,
          status,
          comments,
          delivery_date: deliveryDate || null,
          userid: (user.accesstype === 1) ? user.userid : userid,
          margins: margins || null,
          commission: commission || null,
          items
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        navigate('/control/orders-control');
      } catch (err) {
        setError('Failed to update order');
        console.error(err);
      }
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!order) return <div>Order not found</div>;

  const canEdit = user.accesstype === 0 || order.userid === user.userid;

  if (!canEdit) {
    navigate('/control/orders-control');
    return null;
  }

  return (
    <div className="ceo-container">
      <h2>Edit Order - #{orderid}</h2>
      <Link to={`/control/orders-control/${order.id}`} className="ceo-back-link">Back to Details</Link>

      <form onSubmit={handleSubmit} className="ceo-form">
        <div className="ceo-field">
          <label>Order ID:</label>
          <input type="text" value={orderid} onChange={(e) => setOrderid(e.target.value)} required />
        </div>
        <div className="ceo-field">
          <label>Owner:</label>
          {user.accesstype === 0 ? (
            <select value={userid} onChange={(e) => setUserid(e.target.value)} required>
              <option value="">Select Owner</option>
              {controlUsers.map((u) => (
                <option key={u.userid} value={u.userid}>
                  {u.name}
                </option>
              ))}
            </select>
          ) : (
            <input type="text" value={userid} readOnly />
          )}
        </div>
        <div className="ceo-field">
          <label>Client:</label>
          <select value={idclient} onChange={(e) => setIdclient(e.target.value)} required>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name} ({client.email})</option>
            ))}
          </select>
        </div>
        <div className="ceo-field">
          <label>Creation Date:</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>
        <div className="ceo-field">
          <label>Week:</label>
          <input type="number" value={week} readOnly />
        </div>
        <div className="ceo-field">
          <label>Trimester:</label>
          <input type="number" value={trimester} readOnly />
        </div>
        <div className="ceo-field">
          <label>Status:</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} required>
            <option value="Pending Quote">Pending Quote</option>
            <option value="Pending Payment">Pending Payment</option>
            <option value="Pending Delivery">Pending Delivery</option>
            <option value="Pending Tracking Number">Pending Tracking Number</option>
            <option value="Delivered">Delivered</option>
          </select>
        </div>
        <div className="ceo-field">
          <label>Description:</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="ceo-field">
          <label>Comments:</label>
          <textarea value={comments} onChange={(e) => setComments(e.target.value)} />
        </div>
        <div className="ceo-field">
          <label>Delivery Date:</label>
          <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
        </div>
        <div className="ceo-field">
          <label>Margins:</label>
          <input type="number" step="0.01" value={margins} onChange={(e) => setMargins(e.target.value)} />
        </div>
        <div className="ceo-field">
          <label>Commission:</label>
          <input type="number" step="0.01" value={commission} onChange={(e) => setCommission(e.target.value)} />
        </div>

        <h3>Order Items</h3>
        {items.map((item, index) => (
          <div key={index} className="ceo-item">
            <div className="ceo-item-field">
              <label>Product</label>
              <input
                type="text"
                value={item.product}
                onChange={(e) => handleItemChange(index, 'product', e.target.value)}
              />
            </div>
            <div className="ceo-item-field">
              <label>Brand</label>
              <input
                type="text"
                value={item.brand}
                onChange={(e) => handleItemChange(index, 'brand', e.target.value)}
              />
            </div>
            <div className="ceo-item-field">
              <label>Description</label>
              <input
                type="text"
                value={item.description}
                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
              />
            </div>
            <div className="ceo-item-field-number">
              <label>Cost</label>
              <input
                type="number"
                step="0.01"
                value={item.cost}
                onChange={(e) => handleItemChange(index, 'cost', e.target.value)}
              />
            </div>
            <div className="ceo-item-field-number">
              <label>Price</label>
              <input
                type="number"
                step="0.01"
                value={item.price}
                onChange={(e) => handleItemChange(index, 'price', e.target.value)}
              />
            </div>
            <div className="ceo-item-field-number">
              <label>Quantity</label>
              <input
                type="number"
                value={item.quantity}
                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
              />
            </div>
            <button type="button" className="ceo-remove-btn" onClick={() => handleRemoveItem(index)}>Remove</button>
          </div>
        ))}
        <button type="button" className="ceo-add-item-btn" onClick={handleAddItem}>Add Item</button>

        <button type="submit" className="ceo-submit-btn">Update Order</button>
      </form>
    </div>
  );
};

export default ControlEditOrder;